/**
 * Pure helpers for the ask panel's instructions popover (TRA-1082).
 * Kept apart from the component so they can be tested without a DOM.
 */

import type { AgentPromptResponse } from '@/api/generated/model'

/** The `agent_prompts` kind the review chat reads. Matches the API. */
export const REVIEW_CHAT_PROMPT_KIND = 'review_chat'

/** "Default instructions", or "v3 by name" for the live team version. */
export function versionLine(prompt: AgentPromptResponse): string {
  if (prompt.is_default || prompt.active_version === null || prompt.active_version === undefined) {
    return 'Default instructions'
  }
  const by = prompt.updated_by === null || prompt.updated_by === undefined ? '' : ` by ${prompt.updated_by}`
  return `v${String(prompt.active_version)}${by}`
}

/**
 * Text typed into the popover box, plus the live body it started from.
 * `null` means the box follows whatever is live.
 */
export interface InstructionsDraft {
  text: string
  base: string
}

/**
 * True when the live body moved on after this draft began: a teammate
 * saved. A draft whose text IS the live body is the drafter's own save
 * arriving, not a teammate's.
 */
export function isDraftStale(draft: InstructionsDraft | null, liveBody: string): boolean {
  return draft !== null && draft.base !== liveBody && draft.text !== liveBody
}

/**
 * The draft after `text` is typed into the box. Text equal to the live body
 * is not a draft: the box goes back to following what is live, so a
 * teammate's later save cannot freeze the old body and call it unsaved work.
 */
export function editDraft(
  draft: InstructionsDraft | null,
  text: string,
  liveBody: string,
): InstructionsDraft | null {
  if (text === liveBody) {
    return null
  }
  return { text, base: draft === null ? liveBody : draft.base }
}
